import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { framesService } from '@/features/frames-to-order/services/framesService';
import { manufacturersService } from '../services/manufacturersService';
import { invalidateFrameStatusQueries } from '@/shared/utils/queryUtils';
import type { FrameFilters, MarkAsOrderedInput } from '@/shared/types/domain.types';

/**
 * Query key factory for weekly ordering
 */
export const orderingKeys = {
  all: ['ordering'] as const,
  manufacturerGroups: (filters?: FrameFilters) =>
    [...orderingKeys.all, 'manufacturer-groups', filters] as const,
  manufacturers: () => [...orderingKeys.all, 'manufacturers'] as const,
};

/**
 * Hook to fetch pending frames grouped by manufacturer
 */
export function useManufacturerGroups(filters?: FrameFilters) {
  return useQuery({
    queryKey: orderingKeys.manufacturerGroups(filters),
    queryFn: () => framesService.getFramesGroupedByManufacturer(filters),
  });
}

/**
 * Hook to fetch all manufacturers
 */
export function useManufacturers() {
  return useQuery({
    queryKey: orderingKeys.manufacturers(),
    queryFn: () => manufacturersService.getManufacturers(),
  });
}

/**
 * Hook to mark frames as ordered
 * Includes optimistic updates and cache invalidation
 */
export function useMarkAsOrdered() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: MarkAsOrderedInput) => framesService.markAsOrdered(input),
    onSuccess: () => {
      // Use centralized invalidation to update all frame-status-dependent views
      invalidateFrameStatusQueries(queryClient);
    },
  });
}
